<template>
    <div class="vl-region vl-region--alt vl-region--arrow">
        <div class="vl-layout">
            <!-- Uw label -->
            <div class="card vl-u-spacer--large">
                <div class="vl-grid">
                    <div class="vl-col--6-12 vl-col--1-1--m vl-col--1-1--s">
                        <div class="d-flex align-items-center">
                            <div class="padded">
                                <span class="e-label" :class="labelClass">{{ result.label }}</span>
                            </div>
                            <div class="vl-col--8-12 vl-col--1-1--m vl-col--1-1--s" style="min-width: 0">
                                <div class="padded pr-0">
                                    <h2 class="vl-title--h3 vl-u-text--bold vl-u-spacer--none">
                                        Uw
                                        <span v-if="indicatief">indicatief</span>
                                        energielabel
                                        <span class="vl-icon vl-vi vl-vi-info-filled text-primary ml-1"
                                              id="tooltip-energielabel"></span>
                                        <b-tooltip target="tooltip-energielabel" triggers="hover"
                                                   customClass="vl-tooltip">
                                            <p class="vl-u-text--italic">
                                                <span v-if="indicatief">
                                                    Liever uw echte energielabel kennen? Laat een officieel EPC opmaken
                                                    door een erkend energiedeskundige type A
                                                    (<a href="https://www.energiesparen.be/epcparticulier"
                                                        target="_blank">link</a>) .
                                                    <br><br>
                                                    <span class="vl-u-text--bold">Disclaimer</span><br>
                                                    {{result.disclaimer}}
                                                    <br><br>
                                                </span>
                                                <span class="vl-u-text--bold">Doelstelling 2050: verplicht?</span><br>
                                                Momenteel zijn er nog geen verplichtingen verbonden aan de
                                                doelstelling 2050.
                                                <br><br>
                                                Maar, bent u van plan uw woning te renoveren?
                                                Doe het dan meteen goed en hou rekening met de doelstelling 2050.
                                                Woningen die veraf staan van de doelstelling,
                                                kunnen in de toekomst minder interessant zijn op de vastgoedmarkt en mogelijk in waarde dalen.
                                                Uw woning nu al klaarmaken voor de toekomst levert u onmiddellijk
                                                een lagere energiefactuur en een verhoogd wooncomfort op.
                                            </p>
                                        </b-tooltip>
                                    </h2>
                                    <span class="subtitle">
                                        <strong>{{ result.label }}</strong>
                                        <kwh :label="result.label"/>
                                    </span>
                                    <br><br>
                                    <span class="subtitle">
                                        {{ labelInfo }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="vl-col--6-12 vl-col--1-1--m vl-col--1-1--s d-flex align-items-center">
                        <div class="padded--sm">
                            <div role="alert" class="vl-alert align-items-center mb-0">
                                <svg class="svg-label-small" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 200">
                                    <polygon class="svg-label-small-polygon"
                                             points="0 0,50 100,0 200,350 200,400 100,350 0,0 0"/>
                                    <text class="svg-label-small-text">
                                        <tspan x="150" y="150">A</tspan>
                                    </text>
                                </svg>
                                <h2 class="vl-title--h4 vl-u-text--bold vl-u-spacer--none">
                                    Energielabel A is de doelstelling voor alle woningen in Vlaanderen, en dit tegen
                                    2050.
                                </h2>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Vergelijk -->
            <div class="vl-grid vl-grid--is-stacked">
                <!-- Card links -->
                <div class="vl-col--5-12 vl-col--7-12--m vl-col--1-1--s vl-u-spacer--xsmall">
                    <div class="card" style="min-height: 100%;">
                        <div class="padded padded--sm w100">
                            <div class="vl-u-spacer--medium">
                                <h2 class="vl-title--h3 font-weight-normal vl-u-spacer--none">
                                    <span id="tooltip-percentage-woningen">{{ comparePercentage }}%</span>
                                    van de {{ selectedBebouwingTypeGaugeHeader }} in {{ selectedLocation }}
                                </h2>
                                <b-tooltip target="tooltip-percentage-woningen" triggers="hover"
                                           customClass="vl-tooltip">
                                    <p class="vl-u-text--italic">
                                        Situatie op 1 jan 2022. Gewogen gemiddelde van EPC- en EPB-gegevens.<br>
                                        Bron: Vlaams Energie- en Klimaatagentschap,
                                        <a href="https://www.vlaanderen.be/veka" target="_blank">www.vlaanderen.be/veka</a> .
                                    </p>
                                </b-tooltip>
                                <span class="subtitle">{{ compareInfo }}</span>
                            </div>
                            <gauge class="text-align-center" v-if="resultToCompareTo" :data="resultToCompareTo"
                                   :currentEpcScore="result.epcScore"
                                   @updated="updatePercentage"/>
                            <label for="vl-select-select-2" class="vl-form__label">Mijn woning is gelegen in</label>
                            <vue-bootstrap-typeahead id="vl-select-select-2"
                                                     :data="locations" v-model="locationSearch"
                                                     size="lg"
                                                     placeholder="Zoeken op Vlaanderen, provincies en gemeenten..."
                                                     @hit="getLocationInfo">
                                <template v-slot:suggestion="{ data }">
                                    <div class="typeahead-suggestion">
                                      {{ data }}<br>
                                    </div>
                                </template>
                            </vue-bootstrap-typeahead>
                            <br>
                            <label for="filter-woningtype" class="vl-form__label">En ik wil vergelijken met</label>
                            <select name="filter-woningtype" id="filter-woningtype" class="vl-select vl-select--block"
                                    v-model="selectedBebouwingType" @change="updateBebouwingType">
                                <option disabled value="" selected>Maak een keuze</option>
                                <option v-for="type in bebouwingTypes" :value="type.typeGebouw" :key="type.id">{{
                                    type.typeGebouwDescription }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Cards midden -->
                <div class="vl-col--3-12 vl-col--5-12--m vl-col--1-1--s vl-u-spacer--xsmall"
                     style="display: flex; flex-direction: column; justify-content: space-between;">
                    <div class="card vl-u-spacer--medium">
                        <div class="padded padded--sm pr-1n5">
                            <h2 class="vl-title--h3 font-weight-normal">
                                <span id="tooltip-percentage-pv">22%</span> van de woningen<br>
                                in Vlaanderen
                            </h2>
                            <b-tooltip target="tooltip-percentage-pv" triggers="hover"
                                       customClass="vl-tooltip">
                                <p class="vl-u-text--italic">
                                    Situatie op 1 jan 2023.<br>
                                    Bron: <a href="https://www.vlaanderen.be/veka/energie-en-klimaatbeleid-in-cijfers/energiekaart" target="_blank">Energiekaart</a>.
                                </p>
                            </b-tooltip>
                            <div class="vl-u-display-flex" style="align-items: center;">
                                <img style="float: left; margin: 0 20px 0 0;" src="@/assets/icons/icon-pv.svg"
                                     width="65" alt="Icoon voor PV-panelen">
                                <span class="subtitle">Heeft zonnepanelen.</span>
                            </div>
                        </div>
                    </div>
                    <div class="card vl-u-spacer--medium">
                        <div class="padded padded--sm pr-1n5">
                            <h2 class="vl-title--h3 font-weight-normal">
                                <span id="tooltip-percentage-wpb">12%</span> van de woningen<br>
                                in Vlaanderen
                            </h2>
                            <b-tooltip target="tooltip-percentage-wpb" triggers="hover"
                                       customClass="vl-tooltip">
                                <p class="vl-u-text--italic">
                                    Situatie op 1 jan 2022.<br>
                                    Bron: <a href="https://www.vlaanderen.be/veka/energie-en-klimaatbeleid-in-cijfers/energiekaart" target="_blank">Energiekaart</a>.
                                </p>
                            </b-tooltip>
                            <div class="vl-u-display-flex" style="align-items: center;">
                                <img style="float: left; margin: 0 20px 0 0;" src="@/assets/icons/icon-warmtepomp.svg"
                                     width="65" alt="Icoon voor warmtepomp">
                                <span class="subtitle">Heeft een warmtepomp of warmte-pompboiler.</span>
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="padded padded--sm pr-1n5">
                            <h2 class="vl-title--h3 font-weight-normal">
                                <span id="tooltip-percentage-zonneboiler">2,4%</span> van de woningen<br>
                                in Vlaanderen
                            </h2>
                            <b-tooltip target="tooltip-percentage-zonneboiler" triggers="hover"
                                       customClass="vl-tooltip">
                                <p class="vl-u-text--italic">
                                    Situatie op 1 jan 2022.<br>
                                  Bron: <a href="https://www.vlaanderen.be/veka/energie-en-klimaatbeleid-in-cijfers/energiekaart" target="_blank">Energiekaart</a>.
                                </p>
                            </b-tooltip>
                            <div class="vl-u-display-flex" style="align-items: center;">
                                <img style="float: left; margin: 0 28px 0 9px;"
                                     src="@/assets/icons/icon-zonneboiler.svg" width="48" alt="Icoon voor zonneboiler">
                                <span class="subtitle">Heeft een zonneboiler.</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Card rechts -->
                <div class="vl-col--4-12 vl-col--1-1--m vl-col--1-1--s vl-u-spacer--xsmall">
                    <div class="card" style="min-height: 100%;">
                        <div class="padded padded--sm">
                            <h2 class="vl-title--h3 font-weight-normal vl-u-spacer--none">Wist u dat ...</h2>
                            <div class="vl-typography">
                                <ul>
                                    <li v-if="appartment">
                                        een appartement met een B-label gemiddeld 3,4% duurder verkocht wordt dan een
                                        appartement met een E-label?
                                    </li>
                                    <li v-else>
                                        een eengezinswoning met een B-label gemiddeld 11% duurder verkocht wordt dan een
                                        woning met een E-label?
                                    </li>
                                    <li>
                                        u heel wat financiële ondersteuning krijgt, zoals de
                                        <a href="https://www.vlaanderen.be/epc-labelpremie-bij-energierenovatie-via-fluvius"
                                           target="_blank">EPC-labelpremie</a>, de
                                      <a href="https://www.vlaanderen.be/bouwen-wonen-en-energie/bouwen-en-verbouwen/premies-en-belastingvoordelen/mijn-verbouwpremie" target="_blank">Mijn verbouwpremie</a>, de
                                      <a href="https://www.vlaanderen.be/bouwen-wonen-en-energie/lenen/mijn-verbouwlening"
                                         target="_blank">Mijn verbouwLening </a>
                                        en het
                                        <a href="https://www.vlaanderen.be/bouwen-wonen-en-energie/lenen/renovatiekrediet-met-rentesubsidie-bij-energierenovatie-na-aankoop"
                                           target="_blank">renovatiekrediet met rentesubsidie</a>?
                                    </li>
                                    <li>
                                        vanaf 2020 het dak van iedere woning minimaal moet geïsoleerd zijn?
                                        (<a href="https://www.vlaanderen.be/woningkwaliteitsnormen/dakisolatie-is-verplicht">link</a>)
                                    </li>
                                    <li>
                                        u alle informatie over uw woning kan terugvinden in uw woningpas?
                                        (<a href="https://woningpas.vlaanderen.be/" target="_blank">link</a>)
                                    </li>
                                    <li>
                                        u op de zonnekaart kan kijken hoe geschikt uw dak is voor zonne-energie?
                                        (<a href="https://apps.energiesparen.be/zonnekaart" target="_blank">link</a>)
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import VueScrollTo from 'vue-scrollto';
    import _ from 'underscore';
    import axios from "axios";
    import Kwh from '@/components/partials/Kwh';
    import Gauge from '@/components/partials/Gauge';
    import VueBootstrapTypeahead from 'vue-bootstrap-typeahead';

    export default {
        name: 'Resultaat',
        props: ['result', 'currentSituation', 'indicatief', 'appartment'],
        components: {Kwh, Gauge, VueBootstrapTypeahead},
        data() {
            return {
                locationSearch: 'Vlaanderen',
                selectedLocation: '',
                locations: [],
                selectedBebouwingType: '',
                bebouwingTypes: [],
                resultToCompareTo: null,
                percentages: [],
                epcLabelConfig: {
                  headers: {
                    ['X-Subscription-Key']: process.env.VUE_APP_X_SUBSCRIPTION_KEY
                  },
                  baseURL: process.env.VUE_APP_LABELS_API_URL
                }
            }
        },
        mounted() {
            VueScrollTo.scrollTo('#result', 800, {offset: -44, cancelable: false});
            this.getLocationInfo(this.locationSearch);
        },
        computed: {
            labelClass() {
                return {
                    'e-label--a-plus': this.result.label === 'A+',
                    'e-label--a': this.result.label === 'A',
                    'e-label--b': this.result.label === 'B',
                    'e-label--c': this.result.label === 'C',
                    'e-label--d': this.result.label === 'D',
                    'e-label--e': this.result.label === 'E',
                    'e-label--f': this.result.label === 'F'
                }
            },
            labelInfo() {
                switch (this.result.label) {
                    case 'A+':
                        return 'Uw woning scoort uitstekend en is helemaal klaar voor 2050!';
                    case 'A':
                        return 'Uw woning scoort uitstekend en is helemaal klaar voor 2050!';
                    case 'B':
                        return 'Uw woning scoort al erg goed en is bijna een energielabel A.';
                    case 'C':
                        return 'Uw woning scoort matig en is nog redelijk ver verwijderd van het energielabel A.';
                    case 'D':
                        return 'Uw woning scoort matig en is nog ver verwijderd van het energielabel A.';
                    case 'E':
                        return 'Uw woning scoort slecht en is nog ver verwijderd van het energielabel A.';
                    case 'F':
                        return 'Uw woning scoort erg slecht en is nog ver verwijderd van het energielabel A.';
                    default:
                        return '';
                }
            },
            compareInfo() {
                switch (this.result.label) {
                    case 'A+':
                        return 'Scoort A+ en is klaar voor 2050!';
                    case 'A':
                        return 'Scoort A en is klaar voor 2050!';
                    case 'B':
                        return 'Scoort slechter dan uw woning.';
                    case 'C':
                        return 'Scoort slechter dan uw woning.';
                    case 'D':
                        return 'Scoort beter dan uw woning.';
                    case 'E':
                        return 'Scoort beter dan uw woning.';
                    case 'F':
                        return 'Scoort beter dan uw woning.';
                    default:
                        return '';
                }
            },
            comparePercentage() {
                switch (this.result.label) {
                    case 'A+':
                        return this.percentages[0];
                    case 'A':
                        return this.percentages[0] + this.percentages[1];
                    case 'B':
                        return this.percentages[3] + this.percentages[4] + this.percentages[5] + this.percentages[6];
                    case 'C':
                        return this.percentages[4] + this.percentages[5] + this.percentages[6];
                    case 'D':
                        return this.percentages[0] + this.percentages[1] + this.percentages[2] + this.percentages[3];
                    case 'E':
                        return this.percentages[0] + this.percentages[1] + this.percentages[2] + this.percentages[3] + this.percentages[4];
                    case 'F':
                        return this.percentages[0] + this.percentages[1] + this.percentages[2] + this.percentages[3] + this.percentages[4] + this.percentages[5];
                    default:
                        return '';
                }
            },
            selectedBebouwingTypeGaugeHeader() {
                switch (this.selectedBebouwingType) {
                    case 'Alle types':
                        return 'woningen';
                    case 'Appartement':
                        return 'appartementen';
                    case 'Alle woningen':
                        return 'eengezinswoningen';
                    case 'Open':
                        return 'open bebouwingen ';
                    case 'Halfopen':
                        return 'halfopen bebouwingen ';
                    case 'Gesloten':
                        return 'gesloten bebouwingen ';
                    default:
                        return 'woningen';
                }
            }
        },
        methods: {
            async getLocations(query) {
                axios.get('/epc-labels/locaties?startMet=' + query, this.epcLabelConfig).then(resp => {
                    this.locations = [];
                    resp.data.locaties.forEach(location => {
                        let formattedLocation = location.locatie.toLowerCase().charAt(0).toUpperCase() + location.locatie.toLowerCase().slice(1);
                        let formattedTypeLocatie = location.locatieType.toLowerCase().charAt(0).toUpperCase() + location.locatieType.toLowerCase().slice(1);
                        if (location.locatieType === 'PROVINCIE') {
                            formattedLocation = formattedLocation + ' (' + formattedTypeLocatie + ')';
                        }
                        this.locations.push(formattedLocation);
                    });
                }).catch(() => {
                    this.$emit('axiosError');
                });
            },
            getLocationInfo(event) {
                const loading = this.$loading({lock: true});
                let locatie = event;
                let typeLocatie = 'GEMEENTE';
                if (event.substring(event.length - 12, event.length) === ' (Provincie)') {
                    locatie = event.substring(0, event.length - 12);
                    typeLocatie = 'PROVINCIE';
                } else if (locatie === 'Vlaanderen') {
                    typeLocatie = 'VLAANDEREN';
                }

              axios.get('/epc-labels?locatie=' + locatie.toUpperCase() + '&typeLocatie=' + typeLocatie, this.epcLabelConfig).then(resp => {
                    this.bebouwingTypes = [];
                    resp.data.labels.forEach(bebouwingType => {
                        if (bebouwingType.geldig) {
                            bebouwingType.labels.APlus = (bebouwingType.labels.APlus * 100).toFixed(0);
                            bebouwingType.labels.A = (bebouwingType.labels.A * 100).toFixed(0);
                            bebouwingType.labels.B = (bebouwingType.labels.B * 100).toFixed(0);
                            bebouwingType.labels.C = (bebouwingType.labels.C * 100).toFixed(0);
                            bebouwingType.labels.D = (bebouwingType.labels.D * 100).toFixed(0);
                            bebouwingType.labels.E = (bebouwingType.labels.E * 100).toFixed(0);
                            bebouwingType.labels.F = (bebouwingType.labels.F * 100 + bebouwingType.labels.G * 100 +
                              bebouwingType.labels.H * 100 + bebouwingType.labels.I * 100 + bebouwingType.labels.J * 100 +
                              bebouwingType.labels.K * 100).toFixed(0);
                            bebouwingType.typeGebouw = bebouwingType.typeGebouw.toLowerCase().charAt(0).toUpperCase() + bebouwingType.typeGebouw.slice(1).toLowerCase();
                            bebouwingType.typeGebouwDescription = this.getTypeGebouwDescription(bebouwingType.typeGebouw.toUpperCase());
                            bebouwingType.currentLabel = this.result.label;
                            this.bebouwingTypes.push(bebouwingType);
                        }
                    });
                    this.selectedLocation = event;
                    this.selectedBebouwingType = '';
                    this.bebouwingTypes.forEach(type => {
                        if (type.typeGebouw === 'Alle types') {
                            this.selectedBebouwingType = type.typeGebouw;
                            this.updateBebouwingType(this.selectedBebouwingType, true);
                        }
                    });
                    if (this.selectedBebouwingType === '' && this.bebouwingTypes.length > 0) {
                        this.selectedBebouwingType = this.bebouwingTypes[0].typeGebouw;
                        this.updateBebouwingType(this.selectedBebouwingType, true);
                    }
                    loading.close();
                }).catch(() => {
                    this.$emit('axiosError');
                    loading.close();
                });
            },
            getTypeGebouwDescription(typeGebouw) {
                switch (typeGebouw) {
                    case 'ALLE TYPES':
                        return 'Alle types';
                    case 'APPARTEMENT':
                        return 'Enkel appartementen';
                    case 'ALLE WONINGEN':
                        return 'Enkel eengezinswoningen (alle bebouwingen)';
                    case 'OPEN':
                        return 'Open bebouwingen';
                    case 'HALFOPEN':
                        return 'Halfopen bebouwingen';
                    case 'GESLOTEN':
                        return 'Gesloten bebouwingen';
                }
            },
            updateBebouwingType(event, manuallyTriggered = false) {
                const loading = this.$loading({lock: true});
                const selectedValue = manuallyTriggered ? event : event.target.value;
                const selectedTypeObject = this.bebouwingTypes.filter(type => type.typeGebouw === selectedValue);
                if (selectedTypeObject.length > 0) {
                    this.resultToCompareTo = selectedTypeObject[0];
                }
                loading.close();
            },
            updatePercentage(percentages) {
                this.percentages = percentages;
            },
        },
        watch: {
            locationSearch: _.debounce(function (loc) {
                if (loc.length >= 2) {
                    this.getLocations(loc)
                }
            }, 500),
        }
    }
</script>